import { useCallback, useMemo } from 'react';

import { DataStorageQuery } from 'dashboard-services/api/dataStorage';
import { CommonService } from 'dashboard-services/services';

import properties from 'resources/constants/properties.json';

import { useInfiniteQuery, useQueries, useQuery } from '@tanstack/react-query';

export default (() => {

  const useSearchSymbols = ({ query, access, collapse, symbolValuesExactMatch, withValuesOnly, getQueryBuilder, sortBy, shouldBeLimited, size = CommonService.getRequestSize(), shouldRefetch = true, provider, ...rest }) => (
    useInfiniteQuery({
      ...DataStorageQuery.QUERIES.searchSymbols(
        getQueryBuilder,
        {
          size,
          sortBy,
          collapse,
          query,
          symbolValuesExactMatch,
          withValuesOnly,
          provider,
          access
        }
      ),
      initialPageParam: 0,
      refetchInterval: () => !shouldRefetch || shouldBeLimited() ? undefined : 10000,
      getNextPageParam: lastPage => lastPage.totalSize > lastPage.from + (lastPage.items || []).length ? lastPage.from + (lastPage.items || []).length : undefined,
      ...rest
    })
  )

  const useSearchSymbolsEnvs = ({ query, access, collapse, symbolValuesExactMatch, withValuesOnly, getQueryBuilder, sortBy, shouldBeLimited, size = CommonService.getRequestSize(), shouldRefetch = true, ...rest }) => (
    useInfiniteQuery({
      ...DataStorageQuery.QUERIES.searchSymbolsEnvs(
        getQueryBuilder,
        {
          size,
          sortBy,
          collapse,
          query,
          symbolValuesExactMatch,
          withValuesOnly,
          access
        }
      ),
      refetchInterval: () => !shouldRefetch || shouldBeLimited() ? undefined : 10000,
      ...rest
    })
  )

  const useSearchSymbolKeys = ({ getQueryBuilder, groupName, query, symbols, size, sort, metadata, metadatas, useEs, omitRequiredKeys, enabled = true, provider, ...rest }) =>
    useInfiniteQuery({
      ...DataStorageQuery.QUERIES.getSymbolKeys(
        getQueryBuilder,
        {
          groupName,
          query, 
          symbols,
          size,
          sort,
          metadata,
          metadatas,
          useEs,
          omitRequiredKeys,
          provider
        }
      ),
      initialPageParam: 0,
      getNextPageParam: lastPage => lastPage.totalSize > lastPage.from + (lastPage.items || []).length ? lastPage.from + (lastPage.items || []).length : undefined,
      enabled,
      ...rest
    })

    const useGetColumns = ({ selectedSymbols, getQueryBuilder, provider }) => {
      const queries = useMemo(() => 
        selectedSymbols.map((symbol, symbolIndex) => {
          if(symbol.pattern === undefined || symbol.pattern === true || !symbol.values) {
            return {
              ...DataStorageQuery.QUERIES.getSymbolColumns(getQueryBuilder, {
                groupName: symbol.groupName,
                query: undefined,
                symbols: symbol.symbols,
                metadatas: symbol.metadata,
                size: 50,
                metadata: true,
                useEs: properties.useEsSymbolListing === "true",
                symbolIndex,
                provider
              }),
              select: response => (response?.items || []).map(item => item?.name)
            }
          } else {
            return {
              queryKey: [CommonService.generateUID(), symbolIndex],
              queryFn: async () => Promise.resolve(symbol.values)
            }
          }
        })
      , [selectedSymbols, getQueryBuilder, provider]),
      combine = useCallback(results => ({
        data: results.map(result => result?.data).flat().toUnique().filter(v => !!v).sort((a, b) => a.localeCompare(b)).map(v => ({ label: v, value: v })),
        isPending: results.some(result => result.isPending)
      }), [])

      return useQueries({
        queries,
        combine
      })
    }

  const useGetData = ({ getQueryBuilder, body, size, order, sortSymbols, symbolSize, csv, omitNulls, absoluteSize, ...rest }) => {
    const call = useQuery({
      ...DataStorageQuery.QUERIES.getData(
        getQueryBuilder,
        {
          body,
          size,
          order,
          sortSymbols,
          symbolSize,
          csv,
          omitNulls,
          absoluteSize
        }
      ),
      gcTime: 0,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      ...rest
    })
    return call
  }

  const useGetStreamData = ({ getQueryBuilder, body, size, order, enabled, ...rest }) => {
    const call = useQuery({
      ...DataStorageQuery.QUERIES.getStreamData(
        getQueryBuilder,
        {
          body,
          size,
          order
        }
      ),
      enabled,
      ...rest
    })
    return call
  }

  return { 
    useSearchSymbols,
    useSearchSymbolsEnvs,
    useSearchSymbolKeys,
    useGetColumns,
    useGetData,
    useGetStreamData
  }
})()